// props.itemId is the Item's ID this array belongs to.
// props.counterItems is an [ItemCounter] to show
// props.save is an async function to save edited counter.
// props.delete is a function to delete counter, given an ID.
// props.initialCounterId optionally opens the Edit Hours modal for that
//   counter as soon as the matching row is in counterItems (used by the
//   `?counter=<id>` deep-link from the status email).
// props.onConsumeInitialCounter is called once the deep-link has been
//   honored so the caller can clear the URL param.

import React, { useEffect, useRef } from 'react'
import { DataCard, useStatusMessage, HourSummary, EditHours, PrimaryText } from '.';
import * as Database from '../utilities/Database.mjs';
import { CardHeader } from './CardHeader';
import { primeKeyboard, blurPrimer } from '../utilities/keyboardPrimer';

// Either show there are no items, or show each item.
const Items = ({ counterItems, onClickRow }) => {
  if (counterItems == null || counterItems.length === 0) {
    return (
        <PrimaryText>
          <p>No counters are being tracked. You can add one or more counters and create tasks that are due based on them.</p>
          <p>For example, for a car you might add an <b>Odometer</b> to track when an oil change or tire rotation is due.</p>
        </PrimaryText>
    );
  }

  return counterItems.map( (item, index) => (
    <HourSummary key={item.id} heading={item.name} hours={item.counter} onClick={() => onClickRow(item)} />
  ));
}


function HourMeter(props) {
  // Loading/error UI
  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  // Edit Hours UI.  When to show it, and what to show.
  const [showEditCounter, setShowEditCounter] = React.useState(false);
  const [editCounter, setEditCounter] = React.useState(null);

  // Watch for when unmounted, so don't do things if this object is discarded
  // during an async operation. This happens when the edit UI is up and dismissed
  // due to the object changing and the whole UI gets re-rendered.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    }
  }, [])

  // Honor the `?counter=<id>` deep-link from the email. We track which id we
  // already opened so closing the modal doesn't re-open it on the next
  // re-render. Notifying the parent lets it strip the param from the URL.
  const { initialCounterId, onConsumeInitialCounter, counterItems } = props;
  const handledInitialCounterId = useRef(null);
  useEffect(() => {
    if (!initialCounterId) return;
    if (handledInitialCounterId.current === initialCounterId) return;
    if (!counterItems || counterItems.length === 0) return;

    const match = counterItems.find((c) => c.id === initialCounterId);
    if (!match) return;

    handledInitialCounterId.current = initialCounterId;
    setEditCounter(match);
    setShowEditCounter(true);
    if (onConsumeInitialCounter) {
      onConsumeInitialCounter();
    }
  }, [initialCounterId, counterItems, onConsumeInitialCounter]);


  // Clicked on an counter object, go edit it.
  function onClickRow(item) {
    if (props.delete) {
      setEditCounter(item);
      setShowEditCounter(true);
    }
  }

  // Add counter button hit, add a new counter.
  async function addCounter() {
    // Synchronously focus the hidden primer input INSIDE the tap gesture so
    // mobile browsers open the soft keyboard now. The real Name input takes
    // focus from the primer once the edit modal finishes animating in (see
    // ModalEditForm.onEntered). Without this, iOS Safari refuses to open
    // the keyboard after the `await createItemCounter` below.
    primeKeyboard();

    showText("Adding...");

    // Note: this can throw (e.g. network or security error)
    try {
      const newCounter = {
        counter: 0.0,
        name: Database.NewCounterName,
        itemId: props.itemId
      };
      const createdCounter = await Database.createItemCounter(newCounter);
      console.log(createdCounter);

      // Show in UI
      props.counterItems.push(createdCounter)

      hideStatus();

      // Match the task / item Add UX: pop the edit modal so the user can
      // give the new counter a real name (and tweak the starting reading)
      // immediately, instead of having to find the placeholder row in the
      // list and click it.
      setEditCounter(createdCounter);
      setShowEditCounter(true);
    } catch(err) {
      blurPrimer();
      showError(err);
    }
  }

  // The modal editor's autosave callback. Runs once per autosave flush -- so
  // it must NOT close the modal (see ModalEditForm.jsx): closing here snapped
  // the editor shut ~1.5s after the user stopped typing, mid-edit. Done and
  // the close button are what dismiss it.
  async function saveCounter(counter) {
    counter.id = editCounter.id;

    // Note this can throw. The caller should catch and show the error.
    await props.save(counter);
  }

  async function deleteCounter() {
    await props.delete(editCounter.id);
  }


  function hideEditCounter() {
    hideStatus();
    setShowEditCounter(false);
  }

  return (
    <DataCard
      header=<CardHeader text="Counters" addTapped={props.save && addCounter} />
      body={(
        <div className="text-center">
          <div className="row justify-content-center mt-2">
            <Items counterItems={props.counterItems} onClickRow={onClickRow}/>
          </div>

          {props.save && (
            <>
            <div className="card-footer">
              <StatusMessage/>
            </div>

            <EditHours
              show={showEditCounter}
              onHide={() => hideEditCounter()}
              counter={editCounter}
              save={saveCounter}
              delete={deleteCounter}
            />
            </>
          )}
        </div>
      )}
    />
  )
}

export default HourMeter

